import { Injectable, inject } from '@angular/core';
import { EPermissionKeys } from '@app/shared/enums/permission-keys';
import { UserDTO } from '@app/shared/interfaces/user';
import { AuthService } from '@shared/services/auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private authService = inject(AuthService);

  public evaluate(key: EPermissionKeys | EPermissionKeys[]) {
    return this.evaluateHandler(key);
  }

  /**
   * Route access - the same rule the menu uses (AuthService.haveActionPermission): the org must be
   * licensed for the key, and the user must be Owner/Admin or have the key on a role. For a list of
   * keys, any one of them is enough (as in the menu).
   */
  public evaluateRoutePermission(userData: UserDTO, permissionKey: EPermissionKeys | EPermissionKeys[]): boolean {
    if (!userData) return false;

    const userPermissions: string[] = userData.permissions ?? [];
    const orgPermissions: string[] = userData.orgPermissions ?? [];
    const permissionKeys = Array.isArray(permissionKey) ? permissionKey : [permissionKey];
    if (permissionKeys.length === 0) return true;

    return permissionKeys.some(key =>
      orgPermissions.includes(key) && (userData.isOwner || userData.isAdmin || userPermissions.includes(key))
    );
  }

  public evaluateOrgPermission(key: EPermissionKeys | EPermissionKeys[]) {
    return this.evaluateHandler(key, true);
  }

  private evaluateHandler(key: EPermissionKeys | EPermissionKeys[], onlyOrgPermissions = false) {
    let hasPermission = true;
    if (!key) return hasPermission;
    const permissionCheckMethod = onlyOrgPermissions
      ? this.authService.haveOrgActionPermission.bind(this.authService)
      : this.authService.haveActionPermission.bind(this.authService);

    if (Array.isArray(key)) {
      hasPermission = key.length === 0 ? true : key.some(permissionCheckMethod);
    } else {
      hasPermission = permissionCheckMethod(key);
    }

    return hasPermission;
  }
}
